<template>
  <section v-if="fields" :class="rootClass" :style="rootStyle" :id="rendering?.dataSource">
    <img
      v-if="backgroundSrc"
      class="c-step-by-step-workflow__bg"
      :src="backgroundSrc"
      :alt="backgroundAlt"
      loading="lazy"
    />

    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <div class="c-step-by-step-workflow__inner">
        <header v-if="hasHeader" class="c-step-by-step-workflow__header">
          <JssRichText :field="fields?.title" tag="h2" />
          <RichText
            v-if="fields.description?.value"
            class="c-step-by-step-workflow__description"
            :field="fields.description"
          />
        </header>

        <div
          v-if="columns.length"
          class="c-step-by-step-workflow__panel"
          :class="{ 'c-step-by-step-workflow__panel--with-header': hasHeader }"
          :style="{ '--workflow-columns': columnsCount }"
        >
          <div class="c-step-by-step-workflow__mobile-layout">
            <div
              ref="mobileScrollEl"
              class="c-step-by-step-workflow__mobile-scroll"
              @scroll="updateMobileScroll"
            >
              <div class="c-step-by-step-workflow__grid c-step-by-step-workflow__grid--mobile">
                <article
                  v-for="column in columns"
                  :key="column.id"
                  class="c-step-by-step-workflow__column"
                >
                  <header v-if="hasStageContent(column)" class="c-step-by-step-workflow__stage">
                    <JssText
                      v-if="column.fields.eyebrow?.value"
                      class="c-step-by-step-workflow__eyebrow"
                      :field="column.fields.eyebrow"
                      tag="p"
                    />
                    <JssText
                      v-if="column.fields.title?.value"
                      class="c-step-by-step-workflow__stage-title"
                      :field="column.fields.title"
                      tag="h3"
                    />
                  </header>

                  <div class="c-step-by-step-workflow__cards">
                    <div
                      v-for="(card, cardIndex) in getCards(column)"
                      :key="card.id"
                      class="c-step-by-step-workflow__card"
                      :class="[
                        `c-step-by-step-workflow__card--${getTone(card, cardIndex)}`,
                        {
                          'c-step-by-step-workflow__card--emphasis': card.fields.emphasis?.value,
                        },
                      ]"
                    >
                      <img
                        v-if="getIconSrc(card)"
                        class="c-step-by-step-workflow__card-icon"
                        :src="getIconSrc(card)"
                        :alt="getIconAlt(card)"
                        loading="lazy"
                      />
                      <RichText
                        class="c-step-by-step-workflow__card-body"
                        :field="card.fields.body"
                      />
                    </div>
                  </div>
                </article>
              </div>
            </div>

            <div
              v-if="hasMobileOverflow"
              class="c-step-by-step-workflow__mobile-progress"
              aria-hidden="true"
            >
              <span :style="mobileProgressStyle"></span>
            </div>
          </div>

          <div class="c-step-by-step-workflow__desktop-layout">
            <div class="c-step-by-step-workflow__desktop-stages">
              <header
                v-for="stage in desktopStages"
                :key="`${stage.column.id}-desktop-stage`"
                class="c-step-by-step-workflow__stage c-step-by-step-workflow__stage--desktop"
                :class="{
                  'c-step-by-step-workflow__stage--desktop-empty': !stage.hasContent,
                  'c-step-by-step-workflow__stage--desktop-centered': stage.span > 1,
                }"
                :style="getDesktopStageStyle(stage)"
              >
                <JssText
                  v-if="stage.column.fields.eyebrow?.value"
                  class="c-step-by-step-workflow__eyebrow"
                  :field="stage.column.fields.eyebrow"
                  tag="p"
                />
                <JssText
                  v-if="stage.column.fields.title?.value"
                  class="c-step-by-step-workflow__stage-title"
                  :field="stage.column.fields.title"
                  tag="h3"
                />
              </header>
            </div>

            <div class="c-step-by-step-workflow__desktop-cards">
              <div
                v-for="card in desktopCards"
                :key="`${card.column.id}-${card.item.id}`"
                class="c-step-by-step-workflow__card c-step-by-step-workflow__card--desktop"
                :class="[
                  `c-step-by-step-workflow__card--${getTone(card.item, card.cardIndex)}`,
                  {
                    'c-step-by-step-workflow__card--emphasis': card.item.fields.emphasis?.value,
                  },
                ]"
                :style="getDesktopCardStyle(card)"
              >
                <img
                  v-if="getIconSrc(card.item)"
                  class="c-step-by-step-workflow__card-icon"
                  :src="getIconSrc(card.item)"
                  :alt="getIconAlt(card.item)"
                  loading="lazy"
                />
                <RichText
                  class="c-step-by-step-workflow__card-body"
                  :field="card.item.fields.body"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </ContentGrid>
  </section>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { RichText, Text as JssText } from '@sitecore-jss/sitecore-jss-vue';
import type {
  StepByStepWorkflowCardFields,
  StepByStepWorkflowColumnFields,
  StepByStepWorkflowProps,
} from '@/types/components/StepByStepWorkflow';
import type { ScItem } from '@/types/sitecore';
import utils from '@/utils';

interface NormalizedColumn extends ScItem<StepByStepWorkflowColumnFields> {
  fields: StepByStepWorkflowColumnFields;
}

interface NormalizedCard extends ScItem<StepByStepWorkflowCardFields> {
  fields: StepByStepWorkflowCardFields;
}

interface DesktopCardItem {
  column: NormalizedColumn;
  columnIndex: number;
  cardIndex: number;
  item: NormalizedCard;
}

interface DesktopStageItem {
  column: NormalizedColumn;
  columnIndex: number;
  span: number;
  hasContent: boolean;
}

const props = defineProps<StepByStepWorkflowProps>();
const fields = computed(() => props.fields);
const rendering = computed(() => props.rendering);
const mobileScrollEl = ref<HTMLElement | null>(null);
const mobileProgress = ref(0);
const hasMobileOverflow = ref(false);
let mobileResizeObserver: ResizeObserver | null = null;

const rootClass = computed(() =>
  utils.overall.baseComClass(props.fields, 'c-step-by-step-workflow')
);
const rootStyle = computed(() => utils.overall?.baseComStyle?.(fields.value) || {});
const backgroundSrc = computed(() => fields.value?.backgroundImage?.value?.src || '');
const backgroundAlt = computed(() => fields.value?.backgroundImage?.value?.alt || '');
const hasHeader = computed(
  () => Boolean(fields.value?.title?.value) || Boolean(fields.value?.description?.value)
);

const rawColumns = computed<ScItem<StepByStepWorkflowColumnFields>[]>(() => {
  const columnItems = utils.sc.getFieldObj(fields.value, 'columns');
  return Array.isArray(columnItems) ? columnItems : [];
});

const columns = computed<NormalizedColumn[]>(() =>
  rawColumns.value.filter((column): column is NormalizedColumn => Boolean(column.fields))
);
const columnsCount = computed(() => `${Math.max(columns.value.length, 1)}`);

const getCards = (column: NormalizedColumn): NormalizedCard[] => {
  const cards = utils.sc.getFieldObj(column.fields, 'cards');
  return Array.isArray(cards)
    ? cards.filter((card): card is NormalizedCard => Boolean(card.fields?.body?.value))
    : [];
};

const hasStageContent = (column: NormalizedColumn) =>
  Boolean(column.fields.title?.value) || Boolean(column.fields.eyebrow?.value);

const desktopCards = computed<DesktopCardItem[]>(() =>
  columns.value.flatMap((column, columnIndex) =>
    getCards(column).map((item, cardIndex) => ({
      column,
      columnIndex,
      cardIndex,
      item,
    }))
  )
);

const parseHeaderSpan = (column: NormalizedColumn, columnIndex: number) => {
  const rawSpan = Number(column.fields.headerSpan?.value || 1);
  const span = Number.isFinite(rawSpan) ? Math.floor(rawSpan) : 1;
  return Math.min(Math.max(span, 1), columns.value.length - columnIndex);
};

const desktopStages = computed<DesktopStageItem[]>(() => {
  const stages: DesktopStageItem[] = [];

  for (let columnIndex = 0; columnIndex < columns.value.length;) {
    const column = columns.value[columnIndex];
    const span = parseHeaderSpan(column, columnIndex);
    stages.push({ column, columnIndex, span, hasContent: hasStageContent(column) });
    columnIndex += span;
  }

  return stages;
});

const validTones = ['orange', 'green', 'white'] as const;
type WorkflowCardTone = (typeof validTones)[number];

const isWorkflowCardTone = (tone: string): tone is WorkflowCardTone =>
  validTones.includes(tone as WorkflowCardTone);

const getDefaultTone = (cardIndex: number): WorkflowCardTone => {
  if (cardIndex === 0) return 'orange';
  if (cardIndex === 1) return 'green';
  return 'white';
};

const getTone = (card: NormalizedCard, cardIndex: number): WorkflowCardTone => {
  const toneField = card.fields.tone;
  const tone =
    ('fields' in (toneField || {}) ? toneField?.fields?.phrase?.value : toneField?.value)
      ?.trim()
      .toLowerCase() || '';
  return isWorkflowCardTone(tone) ? tone : getDefaultTone(cardIndex);
};

const getDesktopCardStyle = (card: DesktopCardItem) => {
  return {
    '--workflow-column-start': `${card.columnIndex + 1}`,
    '--workflow-row-start': `${card.cardIndex + 1}`,
  };
};

const getDesktopStageStyle = (stage: DesktopStageItem) => {
  return {
    '--workflow-column-start': `${stage.columnIndex + 1}`,
    '--workflow-column-span': `${stage.span}`,
  };
};

const getIconSrc = (card: NormalizedCard) => card.fields.icon?.value?.src || '';
const getIconAlt = (card: NormalizedCard) =>
  card.fields.icon?.value?.alt || card.fields.icon?.value?.title || '';

const mobileProgressStyle = computed(() => ({
  transform: `scaleX(${Math.max(mobileProgress.value, 0.28)})`,
}));

const updateMobileScroll = () => {
  const el = mobileScrollEl.value;
  if (!el) return;

  const maxScroll = el.scrollWidth - el.clientWidth;
  hasMobileOverflow.value = maxScroll > 1;
  mobileProgress.value = maxScroll > 0 ? el.scrollLeft / maxScroll : 1;
};

onMounted(() => {
  nextTick(updateMobileScroll);

  if (typeof ResizeObserver !== 'undefined' && mobileScrollEl.value) {
    mobileResizeObserver = new ResizeObserver(updateMobileScroll);
    mobileResizeObserver.observe(mobileScrollEl.value);
  }
});

onBeforeUnmount(() => {
  mobileResizeObserver?.disconnect();
});

watch(columns, () => nextTick(updateMobileScroll));
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-step-by-step-workflow {
  position: relative;
  overflow: hidden;
  padding: 40px 0;
  color: $grey-black;
  background: #f3f6f5;

  &__bg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    opacity: 0.7;
    pointer-events: none;
  }

  &__inner {
    position: relative;
    z-index: 1;
    width: 100%;
  }

  &__header {
    max-width: 960px;
    margin: 0 auto;
    text-align: center;
  }

  &__title {
    margin: 0;
    color: #050505;
    font-size: 28px;
    line-height: 1.16;
    letter-spacing: 0;
  }

  &__description {
    margin-top: 14px;
    color: #5f6362;
    font-size: 17px;
    line-height: 1.45;

    p {
      margin: 0;
    }
  }

  &__panel {
    margin-top: 0;
    padding: 20px 18px;
    border: 1px solid rgba(255, 255, 255, 0.92);
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.3);
    box-shadow: 0 24px 64px rgba(82, 98, 98, 0.16);
    backdrop-filter: blur(8px);
  }

  &__panel--with-header {
    margin-top: 24px;
  }

  &__grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
  }

  &__mobile-layout {
    display: none;
  }

  &__mobile-scroll {
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__mobile-progress {
    height: 4px;
    margin-top: 38px;
    overflow: hidden;
    background: rgba(120, 151, 124, 0.28);

    span {
      display: block;
      width: 100%;
      height: 100%;
      background: #719872;
      transform-origin: left center;
      transition: transform 0.12s ease-out;
    }
  }

  &__grid--mobile {
    grid-template-columns: repeat(var(--workflow-columns), clamp(176px, 49vw, 196px));
    width: max-content;
    gap: 0;
  }

  &__grid--mobile &__column:not(:first-child) {
    margin-left: 14px;
  }

  &__grid--mobile &__stage {
    min-height: 86px;
    filter: drop-shadow(0 8px 12px rgba(67, 84, 84, 0.14));
  }

  &__grid--mobile &__column:first-child &__stage {
    clip-path: polygon(0 0, calc(100% - 22px) 0, 100% 50%, calc(100% - 22px) 100%, 0 100%);
  }

  &__grid--mobile &__column:not(:first-child) &__stage {
    margin-left: -6px;
    padding-left: 28px;
    clip-path: polygon(
      0 0,
      calc(100% - 22px) 0,
      100% 50%,
      calc(100% - 22px) 100%,
      0 100%,
      22px 50%
    );
  }

  &__desktop-layout {
    display: block;
    overflow-x: auto;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__column {
    display: grid;
    grid-template-columns: 1fr;
    gap: 14px;
  }

  &__stage {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 64px;
    padding: 14px 20px;
    border: 1px solid rgba(255, 255, 255, 0.92);
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.6);
    box-shadow: 0 9px 20px rgba(86, 98, 98, 0.34);
  }

  &__eyebrow,
  &__stage-title {
    position: relative;
    z-index: 1;
  }

  &__eyebrow {
    margin: 0 0 4px;
    color: #8b8f8d;
    font-size: 14px;
    line-height: 1.2;
    font-weight: 400;
  }

  &__stage-title {
    margin: 0;
    color: #6b9b78;
    font-size: 20px;
    line-height: 1.18;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-align: left;
  }

  &__cards {
    display: grid;
    gap: 16px;
  }

  &__card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 164px;
    padding: 16px 20px;
    border: 0;
    border-left: 2px solid transparent;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.84);
  }

  &__card--orange {
    background: rgba(255, 244, 221, 0.9);
    border-left-color: #f0c875;
  }

  &__card--green {
    background: rgba(231, 245, 239, 0.9);
    border-left-color: #6b9b78;
  }

  &__card--white {
    background: rgba(255, 255, 255, 0.9);
    border-left-color: #80c7d2;
  }

  &__card--emphasis {
    color: #000;
    font-weight: 700;
  }

  &__card-icon {
    display: block;
    width: auto;
    height: 30px;
    margin-bottom: 10px;
  }

  &__card-body {
    text-align: center !important;
    color: #3f4142;
    font-size: 15px;
    line-height: 1.42;

    p {
      margin: 0;
    }

    ul {
      margin: 0;
      padding-left: 18px;
    }
  }

  &__desktop-stages,
  &__desktop-cards {
    min-width: 960px;
  }

  &__desktop-stages {
    --workflow-stage-arrow-depth: 22px;
    --workflow-stage-overlap: 10px;
    display: grid;
    grid-template-columns: repeat(var(--workflow-columns), minmax(0, 1fr));
    gap: 0;
    filter: drop-shadow(0 10px 14px rgba(67, 84, 84, 0.16));
  }

  &__stage--desktop {
    min-height: 80px;
    padding: 0 24px 0 34px;
    border-color: rgba(255, 255, 255, 0.98);
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.6);
    grid-column: var(--workflow-column-start) / span var(--workflow-column-span);
  }

  &__stage--desktop-empty {
    border-color: transparent;
    background: transparent;
    box-shadow: none;
    pointer-events: none;
  }

  &__stage--desktop-centered {
    align-items: center;
    padding-left: 34px;
    padding-right: 34px;
    text-align: center;
  }

  &__stage--desktop:not(:first-child) {
    margin-left: calc(var(--workflow-stage-overlap) * -1);
  }

  &__stage--desktop:not(:last-child) {
    clip-path: polygon(
      0 0,
      calc(100% - var(--workflow-stage-arrow-depth)) 0,
      100% 50%,
      calc(100% - var(--workflow-stage-arrow-depth)) 100%,
      0 100%,
      var(--workflow-stage-arrow-depth) 50%
    );
  }

  &__stage--desktop:first-child {
    padding-left: 20px;
    clip-path: polygon(
      0 0,
      calc(100% - var(--workflow-stage-arrow-depth)) 0,
      100% 50%,
      calc(100% - var(--workflow-stage-arrow-depth)) 100%,
      0 100%
    );
  }

  &__stage--desktop:last-child {
    clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%, var(--workflow-stage-arrow-depth) 50%);
  }

  &__stage--desktop &__stage-title {
    font-size: 16px;
    line-height: 1.24;
    overflow-wrap: anywhere;
    word-break: normal;
  }

  &__desktop-cards {
    display: grid;
    grid-template-columns: repeat(var(--workflow-columns), minmax(0, 1fr));
    grid-auto-rows: minmax(64px, auto);
    gap: 14px 13px;
    margin-top: 14px;
    align-items: stretch;
  }

  &__card--desktop {
    grid-column: var(--workflow-column-start);
    grid-row: var(--workflow-row-start);
    height: 100%;
    min-height: 64px;
    padding: 14px 20px;
  }
  @include tablet {
    &__title {
      font-size: 32px;
    }

    &__description {
      margin-top: 18px;
      font-size: 17px;
    }

    &__panel {
      margin-top: 38px;
      padding: 28px;
      border-radius: 14px;
    }

    &__panel:not(&__panel--with-header) {
      margin-top: 0;
    }

    &__stage {
      min-height: 68px;
      padding: 18px 18px 14px;
    }
  }

  @include tablet-landscape {
    &__title {
      font-size: 32px;
    }

    &__panel {
      padding: 36px;
    }

    &__mobile-layout {
      display: none;
    }

    &__desktop-layout {
      display: block;
    }

    &__stage {
      min-height: 80px;
      padding: 0 24px 0 34px;
      border-radius: 4px;
    }

    &__desktop-stages {
      --workflow-stage-arrow-depth: 22px;
      --workflow-stage-overlap: 10px;
      display: grid;
      grid-template-columns: repeat(var(--workflow-columns), minmax(0, 1fr));
      gap: 0;
      filter: drop-shadow(0 10px 14px rgba(67, 84, 84, 0.16));
    }

    &__stage--desktop {
      grid-column: var(--workflow-column-start) / span var(--workflow-column-span);
      border-color: rgba(255, 255, 255, 0.98);
      background: rgba(255, 255, 255, 0.6);
    }

    &__stage--desktop-empty {
      border-color: transparent;
      background: transparent;
      box-shadow: none;
      pointer-events: none;
    }

    &__stage--desktop-centered {
      align-items: center;
      text-align: center;
      padding-left: 34px;
      padding-right: 34px;
    }

    &__stage--desktop:not(:first-child) {
      margin-left: calc(var(--workflow-stage-overlap) * -1);
    }

    &__stage--desktop:not(:last-child) {
      clip-path: polygon(
        0 0,
        calc(100% - var(--workflow-stage-arrow-depth)) 0,
        100% 50%,
        calc(100% - var(--workflow-stage-arrow-depth)) 100%,
        0 100%,
        var(--workflow-stage-arrow-depth) 50%
      );
    }

    &__stage--desktop:first-child {
      padding-left: 20px;
      clip-path: polygon(
        0 0,
        calc(100% - var(--workflow-stage-arrow-depth)) 0,
        100% 50%,
        calc(100% - var(--workflow-stage-arrow-depth)) 100%,
        0 100%
      );
    }

    &__stage--desktop:last-child {
      clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%, var(--workflow-stage-arrow-depth) 50%);
    }

    &__stage-title {
      font-size: 16px;
      line-height: 1.24;
      word-break: normal;
      overflow-wrap: anywhere;
    }

    &__desktop-cards {
      display: grid;
      grid-template-columns: repeat(var(--workflow-columns), minmax(0, 1fr));
      grid-auto-rows: minmax(64px, auto);
      gap: 14px 13px;
      margin-top: 14px;
      align-items: stretch;
    }

    &__card--desktop {
      grid-column: var(--workflow-column-start);
      grid-row: var(--workflow-row-start);
      height: 100%;
      min-height: 64px;
      padding: 14px 20px;
    }

    &__card-body {
      text-align: center !important;
      font-size: 14px;
    }
  }
}
</style>
